import React, { useContext, useEffect, useState } from 'react';
import { NextPageContext } from 'next';
import { addDays, differenceInDays } from 'date-fns';
import { CancelTokenSource } from 'axios';
import shuffle from 'lodash/shuffle';
import { fetchMemory } from '../integrations/contentful/queries';
import { CmsAsset, CmsMemory } from '../integrations/contentful/types';
import TravellerCard from '../components/travellers/TravellerCard';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import MemoryTitle from '../components/memories/MemoryTitle';
import Container from '../components/grid/Container';
import Quote from '../components/common/Quote';
import MemoryToC from '../components/memories/MemoryToC';
import MemoryPhases from '../components/memories/MemoryPhases';
import Spacing from '../components/common/Spacing';
import BackButton from '../components/navigation/BackButton';
import { t } from '../utils/i18n';
import { navbarLayout } from '../layouts/navbarLayout';
import Breadcrumbs from '../components/head/Breadcrumbs';
import {
  fetchTripPhaseCities,
  fetchTripReservingDetails
} from '../integrations/erp/queries';
import {
  IMTTripPhaseCities,
  IMTTripReservingDetails
} from '../integrations/erp/apiTypes';
import getUseEffectRequestWithCleanup from '../utils/getUseEffectRequestWithCleanup';
import PriceBox from '../components/common/sticky-pricebox/PriceBox';
import StickyBottomBox from '../components/common/sticky-pricebox/StickyBottomBox';
import TravelInfoBox from '../components/common/sticky-pricebox/TravelInfoBox';
import MediaGalleryModal, {
  MediaGalleryItem
} from '../components/media-gallery/MediaGalleryModal';
import { determineIconForMemoryPhase } from '../utils/memories';
import DivButton from '../components/a11y/DivButton';
import {
  baseXUnit,
  color,
  columnTopSpacing,
  font,
  fontSize,
  letterSpacing
} from '../styles/theme';
import { arrayHead } from '../utils/array';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import CmsMediaFixedHeight from '../components/cms/CmsMediaFixedHeight';
import DisplayOn from '../components/common/DisplayOn';
import HorizontalLine from '../components/common/HorizontalLine';
import H from '../components/elements/H';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import {
  DesktopHorizontalList,
  getDesktopHorizontalListTopControls
} from '../components/common/DesktopHorizontalList';
import { fetchMemories } from '../integrations/contentful/queries';
import MemoryCard from '../components/memories/MemoryCard';
import { LoadingState } from '../loading/loadable';
import RenderAsync from '../components/common/RenderAsync';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { configuration } from '../utils/environment';
import { memoryListPageBreadcrumbItem } from './MemoryListPage';
import { TimPage } from './TimPage';
import { SearchContext } from './_app';

interface MemoryPageProps {
  memory?: CmsMemory;
  imtTripId?: number;
  imtTripPhaseCities: IMTTripPhaseCities[];
}

interface MediaGalleryState {
  show: boolean;
  initialSlideIndex: number;
}

async function fetchTripReservingDetailsForMemory(
  imtTripId: number,
  memory: CmsMemory,
  source: CancelTokenSource
): Promise<IMTTripReservingDetails | undefined> {
  const searchStartDate = addDays(new Date(), 1);
  const searchEndDate = addDays(searchStartDate, 7);
  const memoryDuration =
    differenceInDays(
      new Date(memory.fields.endDate),
      new Date(memory.fields.startDate)
    ) + 1; // +1 to get the duration in whole days instead of just the difference

  return fetchTripReservingDetails(
    imtTripId,
    {
      searchStartDate,
      searchEndDate,
      prioritizedDuration: memoryDuration
    },
    source
  );
}

interface RelatedMemories {
  relatedMemories: CmsMemory[];
}

const priceBoxHeight = 9;
export const MEMORY_PAGE_ANALYTICS_CATEGORY = 'Lomamuistosivu';

const RelatedMemories: React.FunctionComponent<RelatedMemories> = ({
  relatedMemories
}) => {
  function renderMemoryCard(memory: CmsMemory) {
    const traveller = arrayHead(memory.fields.travellers);
    if (
      !memory.fields.mainImage ||
      !traveller ||
      !traveller.fields.profilePicture
    ) {
      return null;
    }
    return (
      <MemoryCard
        key={memory.sys.id}
        memory={{
          ...memory.fields,
          mainImage: memory.fields.mainImage
        }}
        traveller={{
          name: traveller.fields.title,
          profilePicture: traveller.fields.profilePicture,
          slug: traveller.fields.slug
        }}
        data-testid="related-memory"
      />
    );
  }
  return (
    <div className="related-memories" data-testid="related-memories">
      <Spacing top={[8, 18, 18]} bottom={[11, 10, 10]}>
        <HorizontalLine expand={false} />
      </Spacing>
      <Container>
        <DisplayOn small medium>
          <Spacing bottom={[5, 4.5]}>
            <H styling="h2">
              <strong>{t('Lisää lomamuistoja sinulle')}</strong>
            </H>
          </Spacing>
        </DisplayOn>
        <DisplayOn small>
          <Row>
            <Col size={24}>
              <HorizontalCardList visibleCards={VisibleCards.One} expand>
                {relatedMemories.map(renderMemoryCard)}
              </HorizontalCardList>
            </Col>
          </Row>
        </DisplayOn>
        <DisplayOn medium>
          <Row>
            <Col size={24}>
              <HorizontalCardList visibleCards={VisibleCards.Two} expand>
                {relatedMemories.map(renderMemoryCard)}
              </HorizontalCardList>
            </Col>
          </Row>
        </DisplayOn>
        <DisplayOn large>
          <DesktopHorizontalList
            elementsPerSlide={3}
            controls={getDesktopHorizontalListTopControls(
              <H styling="h2">
                <strong>{t('Lisää lomamuistoja sinulle')}</strong>
              </H>
            )}
          >
            {relatedMemories.map((memory) => {
              return (
                <Spacing key={memory.sys.id} bottom={4}>
                  {renderMemoryCard(memory)}
                </Spacing>
              );
            })}
          </DesktopHorizontalList>
        </DisplayOn>
      </Container>
    </div>
  );
};

const MemoryPage: TimPage<MemoryPageProps> = ({
  memory,
  imtTripId,
  imtTripPhaseCities
}) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false,
      initialSlideIndex: 0
    });
  const [tripReservationData, setTripReservationData] = React.useState<
    IMTTripReservingDetails | undefined
  >(undefined);
  const [tripReservationDataLoadingState, setTripReservationDataLoadingState] =
    useState<LoadingState>(LoadingState.STARTED);
  useEffect(() => {
    return getUseEffectRequestWithCleanup(async (source: CancelTokenSource) => {
      if (imtTripId && memory) {
        const reservationData = await fetchTripReservingDetailsForMemory(
          imtTripId,
          memory,
          source
        );
        setTripReservationDataLoadingState(
          reservationData ? LoadingState.DONE : LoadingState.FAILED
        );

        setTripReservationData(reservationData);
      } else {
        setTripReservationDataLoadingState(LoadingState.FAILED);
      }
    });
  }, [imtTripId, memory]);

  const shipTrip = imtTripId
    ? configuration.erpShipTripIds.includes(imtTripId)
    : false;

  const [tripDetailsBoxVisibility, setTripDetailsBoxVisibility] =
    React.useState<boolean>(false);

  const searchContext = useContext(SearchContext);

  const [relatedMemoriesLoadingState, setRelatedMemoriesLoadingState] =
    useState<LoadingState>(LoadingState.STARTED);

  const [relatedMemories, setRelatedMemories] = useState<CmsMemory[]>([]);
  useEffect(() => {
    let isCancelled = false;
    const maxRelatedMemories = 9;

    (async () => {
      /* We could do this also server side in getInitialProps, but let's optimze Time to first byte. */
      const searchAnswers = searchContext.answers.getAll();

      // It would be nice to fetch memories with the same tags when there are no search answers.
      // This would give more relevant related memories to user.
      const relatedMemories = (await fetchMemories(searchAnswers))
        .filter((cmsMemory) => {
          if (!memory) {
            return false;
          }
          // Remove current memory from related memories.
          return cmsMemory.sys.id !== memory.sys.id;
        })
        .slice(0, maxRelatedMemories);
      if (!isCancelled) {
        setRelatedMemories(shuffle(relatedMemories));
        setRelatedMemoriesLoadingState(LoadingState.DONE);
      }
    })();

    // Prevent state change on unmount to prevent memory leaks
    return () => {
      isCancelled = false;
    };
  }, [memory, searchContext.answers]);

  if (!memory) {
    return null;
  }

  function onPriceBoxInfoButtonClick() {
    setTripDetailsBoxVisibility(!tripDetailsBoxVisibility);
    trackAnalyticsEvent(
      MEMORY_PAGE_ANALYTICS_CATEGORY,
      'Avattu matkan lisätiedot varauspainikkeesta',
      memory ? memory.fields.title : ''
    );
  }

  const { phases, travellers } = memory.fields;
  const traveller = arrayHead(travellers);
  const destination = arrayHead(memory.fields.destinations);

  const phaseGalleryItems: MediaGalleryItem[] = phases.reduce(
    (allMedia: MediaGalleryItem[], phase, phaseIndex) => {
      const media = [];
      if (phase.fields.mainMedia) {
        media.push(phase.fields.mainMedia);
      }
      media.push(...(phase.fields.media || []));
      const phaseGalleryItems = media.map((media) => ({
        phase: {
          iconName: determineIconForMemoryPhase(
            phase,
            phaseIndex === 0,
            shipTrip
          ),
          title: phase.fields.label,
          scrollTarget: phase.sys.id
        },
        media
      }));
      return allMedia.concat(phaseGalleryItems);
    },
    []
  );
  const mediaGalleryItems: MediaGalleryItem[] = [];
  if (memory.fields.mainImage) {
    mediaGalleryItems.push({
      media: memory.fields.mainImage
    });
  }
  mediaGalleryItems.push(...phaseGalleryItems);
  const canonicalPath = generatePath(Paths.Memory, {
    slug: memory.fields.slug
  });

  const displayMediaInMediaGallery = (media: CmsAsset) => {
    const initialSlideIndex = mediaGalleryItems.findIndex(
      (item) => item.media === media
    );
    if (initialSlideIndex >= 0) {
      setMediaGalleryState({ show: true, initialSlideIndex });
      trackAnalyticsEvent(
        MEMORY_PAGE_ANALYTICS_CATEGORY,
        'Avattu mediagalleriamodaali',
        media.fields.title
      );
    }
  };

  return (
    <div data-cy="memory-page">
      <SEO
        title={
          (memory.fields.seoTitle || memory.fields.title) + ' - Lomamuisto'
        }
        canonicalPath={canonicalPath}
        description={
          memory.fields.seoDescription || memory.fields.leadParagraph
        }
        mainImage={memory.fields.mainImage}
      />
      <Breadcrumbs
        items={[
          memoryListPageBreadcrumbItem,
          { name: memory.fields.title, path: canonicalPath }
        ]}
      />

      <Spacing bottom={6}>
        <Container>
          <Row>
            <Col size={[24, 24, 14]}>
              {mediaGalleryItems.length && (
                <Spacing top={[0, 2, 2, 2]} bottom={3}>
                  <DivButton
                    data-cy="memory-page-main-media"
                    onClick={() => {
                      setMediaGalleryState({
                        show: true,
                        initialSlideIndex: 0
                      });
                      trackAnalyticsEvent(
                        MEMORY_PAGE_ANALYTICS_CATEGORY,
                        'Avattu mediagalleriamodaali',
                        memory.fields.mainImage &&
                          memory.fields.mainImage.fields.title
                      );
                    }}
                  >
                    <div className="main-media-wrapper">
                      {memory.fields.mainImage && (
                        <CmsMediaFixedHeight
                          fixedHeight={[58, 58, 95]}
                          borderRadius={1}
                          maxWidth={98}
                          media={memory.fields.mainImage}
                          hover
                        />
                      )}
                      <div className="main-media-count">
                        {mediaGalleryItems.length}{' '}
                        {t('kuva', 'kuvaa', mediaGalleryItems.length > 1)}
                      </div>
                    </div>
                  </DivButton>
                </Spacing>
              )}
            </Col>
            <Col size={[0, 0, 1]}></Col>
            <Col size={[24, 24, 9]}>
              <Spacing
                top={[2, 2, columnTopSpacing.sideColumn]}
                bottom={[6, 6, 10]}
              >
                {destination && (
                  <MemoryTitle
                    title={memory.fields.title}
                    startDate={memory.fields.startDate}
                    endDate={memory.fields.endDate}
                    destination={destination}
                  />
                )}
              </Spacing>
              <Spacing bottom={[6, 6, 11]} right={[0, 8]}>
                {traveller && (
                  <TravellerCard traveller={traveller} imageSize={[5, 5, 7]} />
                )}
              </Spacing>
            </Col>
          </Row>
          <Row>
            <Col size={24}>
              <DisplayOn small medium>
                <HorizontalLine />
              </DisplayOn>
            </Col>
          </Row>
        </Container>
        <DisplayOn large>
          <HorizontalLine />
        </DisplayOn>
        <Container>
          <Row>
            <Col size={[24, 24, 14]}>
              <Spacing left={[0, 0, 8]} bottom={[6, 6, 23]} top={[7, 7, 16]}>
                <Quote>{memory.fields.leadParagraph}</Quote>
              </Spacing>
            </Col>
            <Col size={[0, 0, 1]}></Col>
            <Col size={[24, 24, 9]}>
              <Spacing top={[0, 0, 16]} bottom={[7, 7, 11]}>
                <MemoryToC phases={phases} shipTrip={shipTrip} />
              </Spacing>
            </Col>
          </Row>
          <Row>
            <Col size={24}>
              <DisplayOn small medium>
                <HorizontalLine />
              </DisplayOn>
            </Col>
          </Row>
        </Container>
        <DisplayOn large>
          <HorizontalLine />
        </DisplayOn>
        <Container>
          <Spacing top={8}>
            <MemoryPhases
              phases={phases}
              onMediaClick={displayMediaInMediaGallery}
              shipTrip={shipTrip}
            />
          </Spacing>
        </Container>
        <RenderAsync
          state={relatedMemoriesLoadingState}
          /* This element is in the bottom of the page so we don't really need skeleton. */
          onLoading={() => <></>}
        >
          {relatedMemories.length > 0 && (
            <RelatedMemories relatedMemories={relatedMemories} />
          )}
        </RenderAsync>
      </Spacing>
      <Spacing bottom={14}>
        <StickyBottomBox>
          {tripReservationData && (
            <TravelInfoBox
              visible={tripDetailsBoxVisibility}
              imtTripPhaseCities={imtTripPhaseCities}
              availabilityDate={tripReservationData.date}
              shipTrip={shipTrip}
            />
          )}
          <PriceBox
            tripReservationData={tripReservationData}
            tripReservationDataLoadingState={tripReservationDataLoadingState}
            infoButtonOnClick={onPriceBoxInfoButtonClick}
            analyticsCategory={MEMORY_PAGE_ANALYTICS_CATEGORY}
            analyticsLabel={memory.fields.title}
            contentfulEntry={memory}
          />
        </StickyBottomBox>
      </Spacing>
      <MediaGalleryModal
        initialSlideIndex={mediaGalleryState.initialSlideIndex}
        items={mediaGalleryItems}
        closeDialog={() =>
          setMediaGalleryState({ ...mediaGalleryState, show: false })
        }
        showDialog={mediaGalleryState.show}
      />
      {/*language=CSS*/}
      <style jsx>{`
        .main-media-wrapper {
          overflow: hidden;
          border-radius: ${baseXUnit(1)};
          position: relative;
          cursor: pointer;
        }

        .main-media-count {
          padding: ${baseXUnit(0.5)} ${baseXUnit(1)};
          opacity: 0.8;
          color: ${color.white};
          text-transform: uppercase;
          background: #000;
          position: absolute;
          right: 0;
          bottom: 0;
          font-size: ${fontSize.px11};
          font-family: ${font.mono};
          letter-spacing: ${letterSpacing.wide};
        }
      `}</style>
    </div>
  );
};

MemoryPage.getLayout = (page) =>
  navbarLayout(
    <BackButton
      defaultTitle={t('Lomamuistot')}
      defaultPath={Paths.MemoryList}
    />,
    page,
    [priceBoxHeight, priceBoxHeight, 0] // Add extra space to footer in small & medium.
  );

MemoryPage.getInitialProps = async function ({ query }: NextPageContext) {
  const memorySlug = String(query.slug);
  const memory = await fetchMemory(memorySlug);
  let imtTripId = undefined;
  let imtTripPhaseCities: IMTTripPhaseCities[] = [];
  if (memory && memory.fields.trip) {
    imtTripId = memory.fields.trip.fields.erpTripId;
    imtTripPhaseCities = await fetchTripPhaseCities(imtTripId);
  }

  const httpErrorCode = !memory ? 404 : undefined;
  return { memory, imtTripId, imtTripPhaseCities, httpErrorCode };
};

export default MemoryPage;
